import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge Tailwind CSS classes with intelligent conflict resolution
 * Business benefit: Consistent styling without class conflicts
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Create a type-safe array of specified length with optional item generation
 *
 * @param length - Positive number for array length
 * @param mapFn - Optional callback function to generate each item (defaults to returning the index)
 * @returns Array of generated items
 *
 * @example
 * // Default: Returns array of indices [0, 1, 2, 3, 4]
 * createArray(5) // [0, 1, 2, 3, 4]
 *
 * @example
 * // With callback: Generate custom items
 * createArray(3, (i) => ({ id: i, name: `Item ${i}` }))
 * // [{ id: 0, name: 'Item 0' }, { id: 1, name: 'Item 1' }, { id: 2, name: 'Item 2' }]
 *
 * @example
 * // Common use case: Skeleton loaders
 * createArray(5, (i) => <Skeleton key={i} />)
 */
export function createArray<T = number>(
  length: number,
  mapFn?: (index: number) => T
): T[] {
  if (length < 0 || !Number.isInteger(length)) {
    throw new Error('createArray: length must be a non-negative integer');
  }

  return Array.from({ length }, (_, index) =>
    mapFn ? mapFn(index) : (index as unknown as T)
  );
}
